import { Dayjs } from "dayjs";
import { IBaseLegacyEntity } from "./base.legacy.entity";
import {
  TimeOffStatus as _TimeOffStatus,
  TimeOffType as _TimeOffType,
} from "hvp-shared";

// Re-export enums and labels from hvp-shared (single source of truth)
export {
  TimeOffStatus,
  TimeOffType,
  TIME_OFF_TYPE_LABELS,
  TIME_OFF_STATUS_LABELS,
} from "hvp-shared";

// Re-export API contracts from hvp-shared
export type {
  CollaboratorTimeOffOverviewResponse,
  VacationPeriodResponse,
  DateTimeOffRequestResponse,
  TimeOffRequestItemResponse,
  CreateTimeOffRequestRequest,
  UpdateTimeOffRequestRequest,
} from "hvp-shared";

// Type alias for backward compatibility — existing code imports this name
export type { CollaboratorTimeOffOverviewResponse as CollaboratorTimeOffOverview } from "hvp-shared";

// Frontend-only: attendance report enum (different domain, kept locally)
export enum AttendanceType {
  ordinary = "Ordinary",
  SimulatedAbsence = "Simulated Absence",
  restDay = "Rest Day",
  vacation = "Vacation",
  sickLeaveIMSS = "IMSS Disability",
  JustifyAbsenceByCompany = "Justified by Company",
  unjustifiedAbsence = "Unjustified Absence",
  dayLeave = "Permission Day (Deducted)",
  NotCounted = "Not Counted",
  PersonalLeave = "Asuntos propios",
  shiftToBeCompensated = "Jornada a reponer",
  AuthorizedUnjustifiedAbsence = "Unjustified Absence Authorized",
  LatePermission = "Late Permission",
  EarlyLeavePermission = "Early Leave Permission",
}

export interface AttendanceTypeInfo {
  description: string;
  name: string;
}

/** Spanish descriptions for each time-off type (used in form help modal) */
export const timeOffRequestTypeDescriptions: Record<_TimeOffType, string> = {
  [_TimeOffType.Vacation]: "Jornada de vacaciones",
  [_TimeOffType.PersonalLeave]:
    "Asuntos propios. Se interpretan como vacaciones",
  [_TimeOffType.SickLeaveIMSS]:
    "Son faltas justificadas legalmente, generalmente las que tienen justificante del IMSS.",
  [_TimeOffType.ShiftToBeCompensated]:
    "Jornada a reponer. Es necesario crear otro time off request con la jornada en la que se repondrá",
  [_TimeOffType.JustifiedAbsenceByCompany]:
    "Inasistencia justificada por la empresa",
  [_TimeOffType.AuthorizedUnjustifiedAbsence]:
    "Falta injustificada autorizada. Tiene los mismos efectos que la falta injustificada pero se toma en cuenta que el colaborador avisó",
  [_TimeOffType.EarlyLeavePermission]: "Permiso de salida anticipada",
  [_TimeOffType.LatePermission]: "Permiso de llegar tarde",
  [_TimeOffType.CompensationShift]: "Reposición de jornada",
  [_TimeOffType.PaidLeave]:
    "Permiso con goce de sueldo (paternidad, matrimonio, duelo, etc.). Paga el sueldo completo, no descuenta, no consume vacaciones ni genera prima vacacional. El motivo se anota en la nota.",
};

// Legacy interface used by old Formik forms and hook. Keep until form is migrated.
export interface TimeOffRequest {
  _id?: string;
  approvedAt?: Date;
  collaborator?: string;
  createdAt?: Date;
  createdBy?: string;
  collaboratorNote?: string;
  managerNote?: string;
  requestedAt?: Date;
  requestedDays: Date[];
  status?: _TimeOffStatus;
  timeOffType: _TimeOffType;
  approvedBy?: string;
  updatedAt?: Date;
  updatedBy?: string;
}

// Frontend-only: collaborator-enriched date entry for UI display
export interface DateTimeOffRequestCollaborator {
  date: Date;
  /** Pre-computed "YYYY-MM-DD" in Mexico timezone for fast calendar lookups */
  dateKeyMx: string;
  id: string;
  timeOffType: _TimeOffType;
  status: _TimeOffStatus;
  collaborator: { name: string; id: string; imgUrl: string };
}

// Frontend-specific typed interfaces for form/entity transformations
export interface TimeOffRequestBaseNew extends IBaseLegacyEntity {
  approvedAt?: Date | string | Dayjs;
  approvedBy?: string;
  collaborator: string;
  collaboratorNote?: string;
  createdBy?: string;
  managerNote?: string;
  requestedAt: Date | string | Dayjs;
  requestedDays: Date[] | string[] | Dayjs[];
  status: _TimeOffStatus;
  timeOffType: _TimeOffType;
  updatedBy?: string;
}

export interface TimeOffRequestRequest extends TimeOffRequestBaseNew {
  id?: string;
  approvedAt?: string;
  requestedAt: string;
  createdAt?: string;
  updatedAt?: string;
  requestedDays: string[];
}

export interface TimeOffRequestResponse extends TimeOffRequestBaseNew {
  id: string;
  approvedAt?: string;
  requestedAt: string;
  createdAt: string;
  updatedAt: string;
  requestedDays: string[];
}

export interface TimeOffRequestEntity extends TimeOffRequestBaseNew {
  requestedDays: Date[];
  approvedAt?: Date;
  requestedAt: Date;
}

export interface TimeOffRequestDayjsEntity extends TimeOffRequestBaseNew {
  requestedDays: Dayjs[];
  approvedAt?: Dayjs;
  requestedAt: Dayjs;
}
